import React, { useState } from "react";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import {
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  Rating,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { Visibility, Delete, Star, Message, Person, Mail, Devices } from "@mui/icons-material";
import { useUpdateFeedbackStatusMutation, useDeleteFeedbackMutation } from "lib/store/service/feedback";
import toast from "react-hot-toast";

const RenderFeedbackActions = ({ feedback, onRefetch }) => {
  const [open, setOpen] = useState(false);
  const [updateStatus, { isLoading: isUpdating }] = useUpdateFeedbackStatusMutation();
  const [deleteFeedback] = useDeleteFeedbackMutation();

  const handleStatusChange = async (newStatus) => {
    try {
      await updateStatus({ feedbackId: feedback._id, status: newStatus }).unwrap();
      toast.success(`Status updated to ${newStatus}`);
      if (onRefetch) onRefetch();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-slate-900 border border-slate-700 shadow-2xl rounded-2xl p-4 pointer-events-auto text-white`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-rose-500/20 text-rose-500 flex items-center justify-center font-bold text-lg">
              ⚠️
            </div>
            <div>
              <p className="text-sm font-bold text-white">Delete Feedback?</p>
              <p className="text-xs text-slate-400">
                Are you sure you want to remove feedback from <strong className="text-cyan-400">{feedback.name}</strong>?
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg"
              onClick={() => toast.remove(t.id)}
            >
              Cancel
            </button>
            <button
              className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg shadow-lg"
              onClick={async () => {
                toast.remove(t.id);
                try {
                  await deleteFeedback(feedback._id).unwrap();
                  toast.success("✅ Feedback deleted successfully!");
                  setOpen(false);
                  if (onRefetch) onRefetch();
                } catch (err) {
                  toast.error("Failed to delete feedback");
                }
              }}
            >
              Confirm Delete
            </button>
          </div>
        </div>
      ),
      { duration: 5000 }
    );
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        startIcon={<Visibility />}
        onClick={() => setOpen(true)}
        sx={{
          fontSize: "11px",
          fontWeight: "bold",
          backgroundColor: "#06b6d4 !important",
          color: "#ffffff !important",
          borderRadius: "8px",
          px: 2,
        }}
      >
        View
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#0f172a",
            color: "#fff",
            borderRadius: "20px",
            border: "1px solid #06b6d4",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e293b", pb: 2 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox display="flex" alignItems="center" gap={1.5}>
              <Message sx={{ color: "#06b6d4" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold">
                Feedback Details
              </VuiTypography>
            </VuiBox>
            <Chip
              label={feedback.platform?.toUpperCase() || "WEB"}
              color="info"
              sx={{ fontWeight: "bold" }}
            />
          </VuiBox>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Person sx={{ color: "#38bdf8" }} />
                <VuiBox>
                  <VuiTypography variant="caption" color="text">USER NAME</VuiTypography>
                  <VuiTypography variant="button" color="white" fontWeight="bold" display="block">
                    {feedback.name}
                  </VuiTypography>
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Mail sx={{ color: "#f59e0b" }} />
                <VuiBox>
                  <VuiTypography variant="caption" color="text">USER EMAIL</VuiTypography>
                  <VuiTypography variant="button" color="white" fontWeight="bold" display="block">
                    {feedback.email}
                  </VuiTypography>
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Star sx={{ color: "#f59e0b" }} />
                <VuiBox>
                  <VuiTypography variant="caption" color="text">RATING</VuiTypography>
                  <Rating value={feedback.rating || 5} readOnly size="small" />
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Devices sx={{ color: "#34d399" }} />
                <VuiBox>
                  <VuiTypography variant="caption" color="text">CATEGORY</VuiTypography>
                  <VuiTypography variant="button" color="success" fontWeight="bold" display="block">
                    {feedback.category || "General"}
                  </VuiTypography>
                </VuiBox>
              </Card>
            </Grid>
          </Grid>

          <VuiBox mb={3} p={2} sx={{ backgroundColor: "#1e293b", borderRadius: "12px", border: "1px solid #334155" }}>
            <VuiTypography variant="caption" color="text" mb={1} display="block">
              FEEDBACK MESSAGE:
            </VuiTypography>
            <VuiTypography variant="button" color="white" sx={{ whiteSpace: "pre-wrap" }}>
              {feedback.message}
            </VuiTypography>
          </VuiBox>

          <VuiBox display="flex" alignItems="center" justifyContent="space-between">
            <VuiTypography variant="button" color="white" fontWeight="bold">
              Update Status:
            </VuiTypography>
            <FormControl size="small">
              <Select
                value={feedback.status || "Pending"}
                onChange={(e) => handleStatusChange(e.target.value)}
                sx={{
                  color: "#fff",
                  backgroundColor: "#1e293b",
                  borderRadius: "8px",
                  "& .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Reviewed">Reviewed</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
              </Select>
            </FormControl>
          </VuiBox>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button
            onClick={handleDelete}
            variant="contained"
            startIcon={<Delete />}
            sx={{ backgroundColor: "#f43f5e !important", color: "#fff !important" }}
          >
            Delete
          </Button>
          <Button onClick={() => setOpen(false)} sx={{ color: "#94a3b8" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const getTableData = (apiResponse, onRefetch) => {
  const feedbacks = apiResponse?.response?.data?.feedbacks || apiResponse?.data?.feedbacks || [];

  const rows = feedbacks.map((item) => {
    const statusColor =
      item.status === "Resolved"
        ? "success"
        : item.status === "Reviewed"
        ? "info"
        : "warning";

    const platformColor =
      item.platform === "creator"
        ? "warning"
        : item.platform === "playground"
        ? "info"
        : "secondary";

    const createdDate = item.createdAt
      ? new Date(item.createdAt).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "Just now";

    return {
      date: (
        <VuiTypography variant="caption" color="text" fontWeight="bold">
          {createdDate}
        </VuiTypography>
      ),
      user: (
        <VuiBox>
          <VuiTypography variant="button" color="white" fontWeight="bold" display="block">
            {item.name}
          </VuiTypography>
          <VuiTypography variant="caption" color="text">
            {item.email}
          </VuiTypography>
        </VuiBox>
      ),
      platform: (
        <Chip
          label={item.platform?.toUpperCase() || "WEB"}
          color={platformColor}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
      category: (
        <VuiTypography variant="caption" color="cyan" fontWeight="bold" sx={{ color: "#06b6d4 !important" }}>
          {item.category || "General"}
        </VuiTypography>
      ),
      rating: <Rating value={item.rating || 5} readOnly size="small" />,
      status: (
        <Chip label={item.status || "Pending"} color={statusColor} size="small" sx={{ fontWeight: "bold" }} />
      ),
      action: <RenderFeedbackActions feedback={item} onRefetch={onRefetch} />,
    };
  });

  return {
    columns: [
      { name: "date", label: "DATE & TIME", align: "left" },
      { name: "user", label: "USER", align: "left" },
      { name: "platform", label: "PLATFORM", align: "center" },
      { name: "category", label: "CATEGORY", align: "center" },
      { name: "rating", label: "RATING", align: "center" },
      { name: "status", label: "STATUS", align: "center" },
      { name: "action", label: "ACTIONS", align: "center" },
    ],
    rows,
  };
};
